'use client';

import { useCallback, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

// Deep-import the concrete module rather than the `@naumu/shared` barrel: the
// barrel re-exports every schemas/*.ts, which drags zod (~299 KiB) into the
// marketing shell's client chunk on EVERY public route. Nothing here needs a
// schema - only the attachment size/type helpers.
import {
	MAX_ATTACHMENTS_PER_MESSAGE,
	getMaxSizeForMime,
	isAudioType,
} from '@naumu/shared/types/attachment';

import { getClientAnalytics } from '@naumu/analytics/analytics/client-analytics';
import { toast } from '@naumu/ui/lib/haptics/haptic-toast';
import { resolveAudioDurationSec } from '@naumu/shared/media/audio-duration';
import type { Project } from '@naumu/shared/types/project-types';

import { clearLandingSpacePinDismissal } from '@naumu/shared/spaces/landing-space-pin';
import {
	writeOnboardingPendingMarker,
} from './onboarding-pending-marker';
import { resolvePlatformHref } from './platform-client-config';
import type { MarketingSpace } from './use-marketing-session';

const LAST_SPACE_KEY = 'naumu-active-project-id';

/**
 * Upload the hero's staged files into the freshly-provisioned space and return
 * the attachment ids to bind to the first onboarding message. Mirrors the
 * in-app `use-attachment-upload` presign → S3 PUT flow, but stateless (no
 * progress/React state - the landing just needs the ids).
 *
 * Works for guests AND signed-in users without any backend change: by the time
 * this runs, provisioning has set the session (guest) or the user is already
 * signed in, so `/api/attachments/presign` resolves the owner from the session
 * cookie - the SAME owner the message-post then uses to resolve the pending
 * uploads (`ownerKey = user.email || user.id`). Per-file failures are skipped,
 * never thrown: landing in the space is the priority, partial attachments are
 * acceptable, and the user can re-upload in chat.
 */
const uploadLandingFiles = async (
	graphId: string,
	threadId: string,
	files?: File[],
): Promise<string[]> => {
	if (!files || files.length === 0) return [];
	const ids: string[] = [];
	for (const file of files.slice(0, MAX_ATTACHMENTS_PER_MESSAGE)) {
		const fileType = file.type || 'application/octet-stream';
		// Advisory client-side size cap (server re-validates against the real
		// plan). 'free' tier is the safe lower bound for an anonymous visitor.
		// `fileName` is passed for the same reason the server passes it: it is
		// what classifies a browser-mistyped text file as agent-readable, and the
		// two sides must agree on the cap.
		if (file.size > getMaxSizeForMime(fileType, 'free', file.name)) continue;
		// Audio carries a duration ceiling on top of the size cap, and the server
		// can only enforce it against a client-reported length. The in-app upload
		// paths send it; the landing has to as well or a dropped multi-hour
		// recording skips the check entirely.
		const audioDurationSec = isAudioType(fileType)
			? await resolveAudioDurationSec(file)
			: 0;
		try {
			const presignRes = await fetch('/api/attachments/presign', {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					graphId,
					threadId,
					fileName: file.name,
					fileType,
					fileSize: file.size,
					...(audioDurationSec > 0 && { audioDurationSec }),
				}),
			});
			if (!presignRes.ok) continue;
			const { attachmentId, uploadUrl } = await presignRes.json();
			// The presigned S3 URL self-authenticates - do NOT send credentials,
			// and Content-Type MUST equal fileType (it's part of the signature).
			const putRes = await fetch(uploadUrl, {
				method: 'PUT',
				headers: { 'Content-Type': fileType },
				body: file,
			});
			if (!putRes.ok) continue;
			ids.push(attachmentId);
		} catch (err) {
			console.warn('Landing file upload failed, skipping', err);
		}
	}
	return ids;
};

/**
 * Probe the session to decide which provisioning endpoint to call. A real
 * signed-in user gets an OWNED space; a guest (`isAnonymous`) or signed-out
 * visitor gets an anonymous one. Resolved at submit time rather than via
 * `useAuth` because marketing routes have no `AuthProvider`. Any failure falls
 * back to the guest path, which always works.
 *
 * Exported because /start (`StartProvision`) needs the same answer BEFORE it
 * provisions: a real signed-in user who lands on the guest door should go to
 * /home, not be handed a brand-new owned space.
 */
export const resolveIsSignedIn = async (): Promise<boolean> => {
	try {
		const res = await fetch('/api/auth/session', { credentials: 'include' });
		if (!res.ok) return false;
		const data = await res.json();
		return !!data?.user && data.user.isAnonymous !== true;
	} catch {
		return false;
	}
};

/**
 * Guest provisioning - the "Try Naumu" / "Drop your pile" entry point.
 *
 * Extracted from the landing `home-page` so every public surface (the header on
 * any marketing page, the new landing hero, the /product CTAs) shares ONE
 * provisioning path: create a space, preserve the landing input for the
 * wizard, then navigate to the workspace beneath its overlay.
 *
 * Branches on session: a real signed-in user hits `provision-space` (a space
 * owned by their account); a guest / signed-out visitor hits `provision-guest`
 * (an anonymous space). Both endpoints return the same shape, so the message
 * post + navigation downstream is identical.
 */
/**
 * Hand off to a space in `apps/web`.
 *
 * A DOCUMENT navigation, never `router.push`. On the landing this always
 * crosses container boundaries - `/spaces/*` belongs to the platform origin,
 * `apps/marketing` has no such route - and a soft navigation there would
 * resolve against marketing's own route manifest and 404. (It happens to work
 * today only because the two apps get different random Next build IDs and Next
 * falls back to an MPA navigation on mismatch; that is an accident, not a
 * contract.) In `apps/web`, where `/start` calls this, it is the same
 * origin and the assign is simply a full reload into the space - which the
 * boot flow does anyway. One code path, correct in both.
 *
 * The path is resolved through `resolvePlatformHref` (which knows, from
 * `configurePlatformClient`, which app this bundle is), because in LOCAL DEV
 * the two apps are two origins: a bare `/spaces/...` assigned from the
 * marketing dev server on :3002 would 404 there instead of entering the space
 * on :3000. Deployed, the two share one origin and the path is returned
 * unchanged.
 */
const goToSpace = (path: string): void => {
	if (typeof window === 'undefined') return;
	window.location.assign(resolvePlatformHref(path));
};

export interface UseGuestProvisioningOptions {
	/**
	 * Onboarding template id (`p1`..`p4`) forwarded to the provisioning body.
	 * Set by the /start spoke pages; the landing composer passes nothing. The
	 * backend validates it against its persona registry and rejects unknown
	 * ids, so callers should resolve through `resolveSpokePersona` first.
	 */
	persona?: string;
}

export const useGuestProvisioning = (
	options: UseGuestProvisioningOptions = {},
): {
	provisionAndGo: (
		firstMessage?: string,
		files?: File[],
		linkedSpace?: MarketingSpace,
	) => void;
	submitting: boolean;
} => {
	const { persona } = options;
	const queryClient = useQueryClient();
	const [submitting, setSubmitting] = useState(false);
	// The RE-ENTRY guard, and the reason it is a ref rather than the `submitting`
	// state: a single user action can reach this callback twice in one tick (a
	// form's onSubmit plus an Enter keydown, a double click, a StrictMode double
	// effect). React state does not update until the next render, so both calls
	// read `submitting === false`, both proceed, and one submit mints TWO spaces
	// - two graphs, two anonymous owners, two OnboardingSessions, and double the
	// provisioning budget. The ref flips synchronously, so the second call is
	// dropped. `submitting` stays only to render the pending state.
	const inFlightRef = useRef(false);
	// The failure toast's Retry has to call the LATEST provisionAndGo, which is
	// the very callback the toast is raised from - a ref breaks that cycle
	// without putting the callback into its own dependency list.
	const provisionRef =
		useRef<
			(
				firstMessage?: string,
				files?: File[],
				linkedSpace?: MarketingSpace,
			) => void
		>(null);

	const provisionAndGo = useCallback(
		async (
			firstMessage?: string,
			files?: File[],
			linkedSpace?: MarketingSpace,
		) => {
			if (inFlightRef.current) return;
			inFlightRef.current = true;
			setSubmitting(true);

			try {
				const trimmedFirst = firstMessage?.trim();
				if (linkedSpace) {
					const threadRes = await fetch('/api/threads', {
						method: 'POST',
						credentials: 'include',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({ graphId: linkedSpace.id }),
					});
					if (!threadRes.ok) {
						throw new Error(`Thread creation failed: ${threadRes.status}`);
					}
					const thread: { id: string } = await threadRes.json();
					const attachmentIds = await uploadLandingFiles(
						linkedSpace.id,
						thread.id,
						files,
					);

					if (trimmedFirst || attachmentIds.length > 0) {
						void fetch(`/api/threads/${thread.id}/messages`, {
							method: 'POST',
							credentials: 'include',
							headers: { 'Content-Type': 'application/json' },
							body: JSON.stringify({
								content: trimmedFirst ?? '',
								contentFormat: 'text',
								...(attachmentIds.length > 0 && { attachmentIds }),
							}),
						}).then(
							(response) => {
								if (!response.ok) {
									console.warn(
										`Failed to post linked landing message: ${response.status}`,
									);
								}
							},
							(error) => {
								console.warn('Failed to post linked landing message', error);
							},
						);
					}

					localStorage.setItem(LAST_SPACE_KEY, linkedSpace.id);
					clearLandingSpacePinDismissal();
					goToSpace(`/spaces/${linkedSpace.slug}/chat/${thread.id}`);
					return;
				}

				getClientAnalytics().track('onboarding_started', { source: 'landing' });
				const isSignedIn = await resolveIsSignedIn();
				const endpoint = isSignedIn
					? '/api/onboarding/provision-space'
					: '/api/onboarding/provision-guest';
				const res = await fetch(endpoint, {
					method: 'POST',
					credentials: 'include',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						// The v2 wizard owns the first visible exchange. The primary
						// onboarding thread stays empty until its C3 batch handoff.
						seedGreeting: false,
						firstMessage: trimmedFirst,
						// `template_persona` rides server-side provisioning analytics;
						// no client event carries it.
						...(persona && { persona }),
					}),
				});
				if (!res.ok) throw new Error(`${endpoint} failed: ${res.status}`);
				const data: {
					slug: string;
					graphId: string;
					graphName: string;
					onboardingThreadId: string;
					wizardSessionId: string | null;
					// Absent on an older backend - `!== false` below keeps those on the
					// wizard path. `false` means the v2 wizard is off server-side and
					// the client owes the pre-wizard flow instead.
					wizardStarted?: boolean;
				} = await res.json();

				// Upload before document navigation, then attach the ids to durable
				// wizard state. The primary thread remains message-free while the
				// overlay runs; the builder can read these pending uploads by id.
				const attachmentIds = await uploadLandingFiles(
					data.graphId,
					data.onboardingThreadId,
					files,
				);

				if (data.wizardStarted === false) {
					// Wizard disabled server-side: the pre-wizard flow. The landing
					// message (and any attachments) goes straight into the onboarding
					// thread so the agent's reply greets them on arrival, and no pending
					// marker is written - there is no overlay to pre-paint. A files-only
					// submit still posts so the attachments land. Recovered from
					// 8c98bd366.
					if (trimmedFirst || attachmentIds.length > 0) {
						try {
							await fetch(`/api/threads/${data.onboardingThreadId}/messages`, {
								method: 'POST',
								credentials: 'include',
								headers: { 'Content-Type': 'application/json' },
								body: JSON.stringify({
									content: trimmedFirst ?? '',
									contentFormat: 'text',
									async: true,
									...(attachmentIds.length > 0 && { attachmentIds }),
								}),
							});
						} catch (err) {
							// Non-fatal - user lands in space and can resend.
							console.warn('Failed to post first onboarding message', err);
						}
					}
				} else if (attachmentIds.length > 0) {
					try {
						const intake = await fetch(
							`/api/onboarding/wizard/${data.wizardSessionId}/intake`,
							{
							method: 'POST',
							credentials: 'include',
							headers: { 'Content-Type': 'application/json' },
							body: JSON.stringify({ attachmentIds }),
							},
						);
						if (!intake.ok) {
							console.warn(`Failed to preserve landing uploads: ${intake.status}`);
						}
					} catch (err) {
						// Non-fatal: the user lands in the wizard and can upload again.
						console.warn('Failed to preserve landing uploads', err);
					}
				}

				localStorage.setItem(LAST_SPACE_KEY, data.graphId);
				// The fresh space is now the last-used one - the landing pin should
				// point at it on the next visit, so any "start fresh" dismissal ends.
				clearLandingSpacePinDismissal();
				// Pre-populate the projects cache so ProjectContext sees the new
				// space immediately. Prepend rather than replace - a signed-in user
				// already has spaces in the cache and shouldn't have them clobbered
				// (a guest's cache is empty, so this is just `[newSpace]` for them).
				queryClient.setQueryData<Project[]>(['projects'], (prev) => {
					const next = prev ? [...prev] : [];
					if (next.some((p) => p.id === data.graphId)) return next;
					return [
						{
							id: data.graphId,
							name: data.graphName,
							slug: data.slug,
							role: 'OWNS',
							memberRole: 'owner',
						},
						...next,
					];
				});
				queryClient.invalidateQueries({ queryKey: ['projects'] });
				// Only when a wizard actually started: the space shell reads this
				// synchronously on the way in and paints the wizard's blackroom FIRST
				// (no sidebar skeleton). With nothing answered yet the first slide is
				// the WELCOME cover, so the skeleton is a bare room; landing text or
				// files resume the wizard past it. On the pre-wizard flow there is no
				// overlay, so a marker would blackroom a space that just goes to chat.
				if (data.wizardStarted !== false) {
					writeOnboardingPendingMarker(
						data.slug,
						trimmedFirst || attachmentIds.length > 0 ? 'wizard' : 'cover',
					);
				}
				goToSpace(`/spaces/${data.slug}/chat/${data.onboardingThreadId}`);
			} catch (err) {
				console.error(
					linkedSpace ? 'Linked conversation failed' : 'Provision failed',
					err,
				);
				// The failure must be VISIBLE and recoverable in one click (W7 F3):
				// provisioning intermittently 500s, and the pre-W7 behaviour was a
				// button that quietly went back to idle, which reads as "the product
				// is broken" rather than "that request failed". The in-flight guard
				// is released BEFORE the toast so Retry is not swallowed by it, and
				// Retry goes through `provisionRef` so it always runs the current
				// closure.
				inFlightRef.current = false;
				setSubmitting(false);
				toast.error(
					linkedSpace
						? 'Could not start the conversation.'
						: 'Could not set up your space.',
					{
						description: 'Something went wrong on our side. Try again?',
						action: {
							label: 'Retry',
							onClick: () => {
								void provisionRef.current?.(firstMessage, files, linkedSpace);
							},
						},
					},
				);
			}
		},
		// No `submitting` dependency: the guard is the ref above, and leaving the
		// state out holds the callback identity stable across the pending flip
		// (effect-driven callers like /start re-run on every identity change).
		[queryClient, persona],
	);
	provisionRef.current = provisionAndGo;

	return { provisionAndGo, submitting };
};
